import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Check, PackageCheck, Plus, Search, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import {
  OrderPicker,
  PickerModal,
  orderLabel as formatOrderLabel,
} from '../components/OrderPicker';
import InspectReturns from '../components/returns/InspectReturns';
import { PageHeader } from '../components/ui/PageHeader';

/** Response shape of RETURN_RECEIVE — since migration 135 the parts go to Zone Q and wait for
 *  inspection; no credit note and no money move at receipt. */
interface ReceiveResult {
  id: string;
  status: string;
  returnedTTC: number;
}

/** Line of GET /orders/:id — the product picker only ever offers lines of the picked order. */
interface OrderLineRow {
  id: string;
  productId: string | null; // null on a SHIPPING line — never returnable
  lineType?: 'GOODS' | 'SHIPPING';
  productSku?: string | null;
  productName?: string | null;
  quantity: number;
  quantityDelivered?: number;
}

const euro = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

/**
 * Screens 37-38 — Customer Returns.
 * RETURN_APPROVE authorizes only. RETURN_RECEIVE puts the parts in Zone Q; the inspection
 * (migration 135) accepts or refuses each unit, mints the credit note for the accepted ones and
 * hands the refund to the original payment channel. RETURN_REJECT is terminal and records the reason.
 */
export default function Returns() {
  const { t } = useTranslation();
  const [orderId, setOrderId] = useState('');
  const [orderLabel, setOrderLabel] = useState('');
  const [productId, setProductId] = useState('');
  const [productLabel, setProductLabel] = useState('');
  const [pickingOrder, setPickingOrder] = useState(false);
  const [pickingProduct, setPickingProduct] = useState(false);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [returnId, setReturnId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const { showApiError, push } = useToast();
  const canApprove = usePermission(Permission.CUSTOMER_RETURN_APPROVE);
  const canReceive = usePermission(Permission.STOCK_RECEIVE);

  const create = async () => {
    try {
      const { data } = await apiClient.post<{ id: string }>(
        '/returns',
        { orderId, reason, lines: [{ productId, quantity: qty, reason }] },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('returns.created', { id: data.id }) });
      setReturnId(data.id);
    } catch (err) {
      showApiError(err);
    }
  };

  const transition = async (action: 'approve' | 'reject', body: unknown = {}) => {
    try {
      await apiClient.post(`/returns/${returnId}/${action}`, body, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({ tone: 'success', message: t('returns.transitioned', { action }) });
    } catch (err) {
      showApiError(err);
    }
  };

  const approve = () =>
    transition('approve').then(() =>
      push({
        tone: 'info',
        message: t('returns.approvedNote'),
      })
    );

  const reject = async () => {
    if (!rejectReason.trim()) {
      push({ tone: 'warning', message: t('returns.rejectReasonRequired') });
      return;
    }
    await transition('reject', { reason: rejectReason.trim() });
  };

  /** RETURN_RECEIVE — into Zone Q; the inspection panel below picks it up. */
  const receive = async () => {
    try {
      const { data } = await apiClient.post<ReceiveResult>(
        `/returns/${returnId}/receive`,
        { lines: [{ productId, qty }] },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('returns.received', { amount: euro(data.returnedTTC) }) });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('returns.title')} />
      <section className="border rounded p-4 bg-card space-y-2 text-sm">
        <h2 className="font-semibold">{t('returns.step1')}</h2>
        {/* The IDs are UUIDs — typing one by hand is where this screen used to lose people, so
            each field carries a picker. Choosing an order also scopes the product picker to that
            order's lines, which is the only set a return can legitimately draw from. */}
        <div className="flex items-center gap-2">
          <Input
            className="flex-1 font-mono"
            value={orderId}
            onChange={(e) => {
              setOrderId(e.target.value);
              setOrderLabel('');
            }}
            placeholder={t('returns.orderIdPlaceholder')}
          />
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8 shrink-0"
            aria-label={t('returns.selectOrder')}
            title={t('returns.selectOrder')}
            onClick={() => setPickingOrder(true)}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        {orderLabel && (
          <p className="text-xs text-muted-foreground">
            {t('returns.orderLabel', { label: orderLabel })}
          </p>
        )}

        <div className="flex items-center gap-2">
          <Input
            className="flex-1 font-mono"
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setProductLabel('');
            }}
            placeholder={t('returns.productIdPlaceholder')}
          />
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8 shrink-0"
            disabled={!orderId}
            aria-label={t('returns.selectProduct')}
            title={orderId ? t('returns.selectProduct') : t('returns.pickOrderFirst')}
            onClick={() => setPickingProduct(true)}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        {productLabel && <p className="text-xs text-muted-foreground">{productLabel}</p>}
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            className="w-20"
            title={t('returns.quantityReturned')}
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
          />
          <Input
            className="flex-1"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('returns.reasonPlaceholder')}
          />
        </div>
        <Button size="sm" onClick={create}>
          <Plus className="h-4 w-4" />
          {t('returns.createRequested')}
        </Button>
      </section>

      {returnId && (
        <section className="border rounded p-4 bg-card space-y-3 text-sm">
          <h2 className="font-semibold">
            {t('returns.step2')} <span className="font-mono text-xs">{returnId}</span>
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            {canApprove && (
              <Button variant="success" size="sm" onClick={approve}>
                <Check className="h-4 w-4" />
                {t('common.approve')}
              </Button>
            )}
            {canReceive && (
              <Button size="sm" onClick={receive}>
                <PackageCheck className="h-4 w-4" />
                {t('returns.receive')}
              </Button>
            )}
          </div>

          {canApprove && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="min-w-[16rem] flex-1"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('returns.rejectReasonPlaceholder')}
              />
              <Button variant="destructive" size="sm" onClick={reject}>
                <X className="h-4 w-4" />
                {t('common.reject')}
              </Button>
            </div>
          )}
        </section>
      )}

      {canReceive && <InspectReturns refreshKey={refreshKey} />}

      <p className="text-xs text-muted-foreground">
        <Trans i18nKey="returns.footnote" components={{ 1: <strong /> }} />
      </p>

      {pickingOrder && (
        <OrderPicker
          title={t('returns.chooseOrder')}
          onClose={() => setPickingOrder(false)}
          onPick={(o) => {
            setOrderId(o.id);
            setOrderLabel(formatOrderLabel(o));
            // A product only makes sense relative to its order.
            setProductId('');
            setProductLabel('');
            setPickingOrder(false);
          }}
        />
      )}
      {pickingProduct && (
        <ProductPicker
          orderId={orderId}
          onClose={() => setPickingProduct(false)}
          onPick={(l) => {
            setProductId(l.productId!);
            setProductLabel(
              t('returns.productLabel', {
                name: l.productName ?? l.productId,
                sku: l.productSku ? ` (${l.productSku})` : '',
                qty: l.quantity,
              })
            );
            setQty(l.quantity);
            setPickingProduct(false);
          }}
        />
      )}
    </div>
  );
}

/** Pick the returned product from the order's own lines. SHIPPING lines carry no product and a
 *  line that never shipped has nothing to come back, so both are left out. */
function ProductPicker({
  orderId,
  onPick,
  onClose,
}: {
  orderId: string;
  onPick: (l: OrderLineRow) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [lines, setLines] = useState<OrderLineRow[] | null>(null);

  useEffect(() => {
    apiClient
      .get<{ lines?: OrderLineRow[] }>(`/orders/${orderId}`)
      .then(({ data }) =>
        setLines((data.lines ?? []).filter((l) => l.productId && l.lineType !== 'SHIPPING'))
      )
      .catch((err) => {
        showApiError(err);
        setLines([]);
      });
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PickerModal title={t('returns.chooseProduct')} onClose={onClose}>
      {lines === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('returns.noReturnable')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left py-1">{t('common.product')}</th>
              <th className="text-right">{t('returns.ordered')}</th>
              <th className="text-right">{t('returns.delivered')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr
                key={l.id}
                onClick={() => onPick(l)}
                className="border-t cursor-pointer hover:bg-muted/40"
              >
                <td className="py-1">
                  <div className="font-medium">{l.productName ?? l.productId}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {l.productSku ?? l.productId}
                  </div>
                </td>
                <td className="text-right">{l.quantity}</td>
                <td className="text-right">{l.quantityDelivered ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PickerModal>
  );
}
